import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Split Excel Sheets Step
 *
 * Takes an Excel document and produces one child Document per worksheet.
 * Outputs flat parallel arrays for fanout — downstream steps receive
 * one child workflow per sheet when wired to single-value inputs.
 */
export const splitExcelSheetsStep = defineStepConfig({
  type: "split_excel_sheets",
  name: "Split Excel Sheets",
  description: "Split an Excel file into one document per worksheet",
  category: "Transformation",

  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "The Excel document to split into sheets",
    ),
  }),

  outputSchema: z.object({
    document: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .describe("Child document per sheet"),
    sheetName: z.array(z.string()).describe("Worksheet name per item"),
    sheetIndex: z
      .array(z.number())
      .describe("0-based worksheet index per item"),
  }),

  configSchema: z.object({
    sheetFilter: z
      .string()
      .optional()
      .describe(
        "Optional regex pattern to filter sheet names (e.g. '^Data' to only include sheets starting with 'Data')",
      ),
    skipEmptySheets: z
      .boolean()
      .optional()
      .default(true)
      .describe("Skip worksheets that have no data rows"),
    outputFormat: z
      .enum(["csv", "xlsx"])
      .optional()
      .default("csv")
      .describe("Format for child documents: CSV or XLSX"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "3 minutes",
    retry: {
      maximumAttempts: 2,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
